import React from 'react';
import cn from 'classnames';

import { Tooltip } from '@material-ui/core';

import ColorStyles from '@components/ColorStyles';
import { ReactComponent as RobotSvg } from './assets/images_robot.svg';
import { ReactComponent as AttributeSlotSvg } from './assets/images_attr_slots.svg';
import { ReactComponent as AudienceSvg } from './assets/images_audience.svg';
import { ReactComponent as CalendarSvg } from './assets/images_calendar.svg';
import { ReactComponent as VariantSvg } from './assets/images_variant.svg';
import { ReactComponent as ReadOnlySvg } from './assets/images_read_only.svg';
import SortInactivePng from './assets/images_sort_both.png';
import SortAscPng from './assets/images_sort_asc.png';
import SortDescPng from './assets/images_sort_desc.png';
import { ReactComponent as OutOfStockSvg } from './assets/images_out_of_stock.svg';
import { ReactComponent as ProductSlotsSvg } from './assets/images_product_slots.svg';
import InfoOutlinedPng from './assets/images_info_outlined.png';
import WarningOutlinedPng from './assets/images_warning_outlined.png';
import { ReactComponent as ForceAddToRecallSvg } from './assets/add_to_recall.svg';
import { ReactComponent as ForceAddToRecallUndoSvg } from './assets/add_to_recall_undo.svg';
import { ReactComponent as ToolTipIconSvg } from './assets/tool_tip_icon.svg';
import { ReactComponent as SiteAccountSVG } from './assets/site_account.svg';
import { ReactComponent as LanguageSVG } from './assets/language.svg';
import { ReactComponent as NextStepSVG } from './assets/next_step.svg';
import { ReactComponent as PartialCompletionSVG } from './assets/partial_completion.svg';
import { ReactComponent as ScheduledSVG } from './assets/scheduled.svg';
import { ReactComponent as SuccessfulCompletionSVG } from './assets/successful_completion.svg';
import { ReactComponent as LeftArrowSVG } from './assets/left_arrow.svg';
import { ReactComponent as TimeSVG } from './assets/time.svg';
import { ReactComponent as LiveIconSvg } from './assets/images_live.svg';
import { ReactComponent as BackIconSvg } from './assets/images_back.svg';
import { ReactComponent as WarningColorIcon } from './assets/color-warning-icon.svg';
import { ReactComponent as Refresh } from './assets/refresh.svg';
import { ReactComponent as OrangeLock } from './assets/orange_lock.svg';
import { ReactComponent as OrangeUnlock } from './assets/orange_unlock.svg';
import { ReactComponent as WarningToast } from './assets/warning_toast.svg';
import { ReactComponent as ThreeDotsSvg } from './assets/three_dots.svg';
import { ReactComponent as CopySvg } from './assets/copy.svg';
import { ReactComponent as BinSvg } from './assets/bin.svg';
import { ReactComponent as GrabberSvg } from './assets/grabber.svg';
import { ReactComponent as VariantSvg2 } from './assets/variant_v2.svg';
import { ReactComponent as AudienceSvgV2 } from './assets/audience_v2.svg';
import { ReactComponent as CalendarSvgV2 } from './assets/calendar_v2.svg';
import { ReactComponent as ReadOnlySvgV2 } from './assets/read_only_v2.svg';
import { ReactComponent as DownCarrotSvgV2 } from './assets/down_carrot_v2.svg';
import { ReactComponent as InactiveCheckboxSvgV1 } from './assets/inactive_checkbox_v1.svg';
import { ReactComponent as SuccessCheckSvgV2 } from './assets/success_check_v2.svg';
import { ReactComponent as CrossCheckSvgV2 } from './assets/cross_check_v2.svg';
import { ReactComponent as GreaterThanLight } from './assets/icon-greater-than-light.svg';
import { ReactComponent as GreaterThanThick } from './assets/icon-greater-than-thick.svg';
import { ReactComponent as ImageSearch } from './assets/image_search.svg';

import './brIcons.scss';

interface BrIconsProps {
  className: string;
  colorStyle: string;
  disableHover: boolean;
  isSubComp: boolean;
  onClick: () => void;
  colored: boolean; // TODO: deprecated (see comment at EOF about "iconComponents");
  tooltipText: string;
}

/**
  generates final icon class to support additional properties
  baseClass (str) :=> base class for the icon, often unique and defines the icon (for fa)
  children (react) :=> any sub components to be wrapped inside the class, used for composite and svg icon

  resulting icon class will accept properties including
  colorStyle (only impacts fa), isSubComp, className, and disableHover
*/
const genIcon = (baseClass: string, children?: JSX.Element) => function BrIcon(props: BrIconsProps): JSX.Element {
  // accepted props to <BrIcons.xyz a={} b={} />
  const { className, colorStyle, isSubComp, disableHover, onClick, tooltipText } = props;
  const iconClass = cn('br-icon', baseClass, className, colorStyle, {
    'sub-comp': isSubComp,
    'no-hover': disableHover,
  });

  const iconWithToolTip = (
    <Tooltip arrow title={tooltipText} placement="top">
      <i className={iconClass} data-id={`br-icon-${baseClass}`} onClick={onClick}>
        {children}
      </i>
    </Tooltip>
  );

  const iconWithoutTooltip = (
    <i className={iconClass} data-id={`br-icon-${baseClass}`} onClick={onClick}>
      {children}
    </i>
  );
  return tooltipText ? iconWithToolTip : iconWithoutTooltip;
};

// This converts base fa icons to icon classes
const faIconClassGenerator = (faIcons: Record<string, string>): Record<string, JSX.Element> => {
  const generatedIcons: Record<string, any> = {};
  Object.keys(faIcons).forEach((iconKey) => {
    const upperCaseKey = iconKey.charAt(0).toUpperCase() + iconKey.slice(1);
    const iconClass = cn('fa', faIcons[iconKey]);
    generatedIcons[upperCaseKey] = genIcon(iconClass);
  });
  return generatedIcons;
};

// the following base icons will be converted to icon class that accepts ColorStyles
const faBaseIcons = {
  calendar: 'fa-calendar',
  search: 'fa-search',
  clear: 'fa-times-circle',
  boost: 'fa-arrow-circle-up',
  bury: 'fa-arrow-circle-down',
  check: 'fa-check',
  cross: 'fa-times',
  edit: 'fa-pencil',
  downCaret: 'fa-caret-down',
  leftAngle: 'fa-angle-left',
  rightAngle: 'fa-angle-right',
  warning: 'fa-exclamation-triangle',
  notice: 'fa-exclamation-circle',
  view: 'fa-eye',
  viewHide: 'fa-eye-slash',
  info: 'fa-info-circle',
  stop: 'fa-ban',
  lock: 'fa-lock',
  unlock: 'fa-unlock',
  blacklist: 'fa-ban',
  undo: 'fa-undo',
  ellipsis: 'fa-ellipsis-h',
  include: 'fa-plus',
  exclude: 'fa-minus',
  cog: 'fa-cog',
  question: 'fa-question-circle',
  star: 'fa-star',
  holoStar: 'fa-star-o',
  extlink: 'fa-external-link',
  delete: 'fa-trash',
  filter: 'fa-filter',
  copy: 'fa-files-o',
  // animated
  spinner: 'fa-circle-o-notch fa-spin',
};

const icons: Record<string, any> = {
  // generates simple fa based icons
  ...faIconClassGenerator(faBaseIcons),

  // composite fa icons
  LockInPlace: genIcon(
    'fa-stack fa fa-sm',
    <span>
      <i className="fa fa-lock fa-stack-2x" style={{ color: 'black' }} />
      <i className="fa fa-hashtag fa-stack-1x" style={{ color: 'white' }} />
    </span>,
  ),
  BoostLock: genIcon(
    'fa-stack fa fa-sm',
    <span>
      <i className="fa fa-lock fa-stack-2x" style={{ color: 'green' }} />
      <i className="fa fa-arrow-up fa-stack-1x lock-arrow" style={{ color: 'white' }} />
    </span>,
  ),
  BuryLock: genIcon(
    'fa-stack fa fa-sm',
    <span>
      <i className="fa fa-lock fa-stack-2x" style={{ color: 'orange' }} />
      <i className="fa fa-arrow-down fa-stack-1x lock-arrow" style={{ color: 'white' }} />
    </span>,
  ),

  // svg based icons
  Robot: genIcon('br-robot', <RobotSvg />),
  AttributeSlot: genIcon('br-attr-slot', <AttributeSlotSvg />),
  Audience: genIcon('br-audience', <AudienceSvg />),
  Calendar: genIcon('br-calendar', <CalendarSvg />),
  Variant: genIcon('br-variant', <VariantSvg />),
  OutOfStock: genIcon('br-out-of-stock', <OutOfStockSvg />),
  ProductSlots: genIcon('br-product-slots', <ProductSlotsSvg />),
  ReadOnly: genIcon('br-read-only', <ReadOnlySvg />),
  ForceAddToRecall: genIcon(
    'br-force-add-to-recall',
    <ForceAddToRecallSvg />,
  ),
  ForceAddToRecallUndo: genIcon(
    'br-force-add-to-recall-undo',
    <ForceAddToRecallUndoSvg />,
  ),
  SiteAccount: genIcon('br-site-account', <SiteAccountSVG />),
  Language: genIcon('br-language', <LanguageSVG />),
  NextStep: genIcon('br-next-step', <NextStepSVG />),
  PartialCompletion: genIcon(
    'br-partial-completion',
    <PartialCompletionSVG />,
  ),
  Scheduled: genIcon('br-scheduled', <ScheduledSVG />),
  SuccessfulCompletion: genIcon(
    'br-successful-completion',
    <SuccessfulCompletionSVG />,
  ),
  LeftArrow: genIcon('br-left-arrow', <LeftArrowSVG />),
  TimeSVG: genIcon('br-time', <TimeSVG />),
  LiveIcon: genIcon('br-live-icon', <LiveIconSvg />),
  BackIcon: genIcon('br-live-icon', <BackIconSvg />),
  ToolTipIcon: genIcon('br-tool-tip-icon', <ToolTipIconSvg />),
  ThreeDots: genIcon('br-three-dots', <ThreeDotsSvg />),
  WarningColorIcon: genIcon('br-warning-color-icon', <WarningColorIcon />),
  Refresh: genIcon('br-refresh', <Refresh />),
  OrangeLock: genIcon('br-live-icon', <OrangeLock />),
  OrangeUnlock: genIcon('br-live-icon', <OrangeUnlock />),
  WarningToastIcon: genIcon('br-live-icon', <WarningToast />),
  BrBin: genIcon('br-bin-icon', <BinSvg />),
  BrCopy: genIcon('br-copy-icon', <CopySvg />),
  BrGrabber: genIcon('br-grabber-icon', <GrabberSvg />),
  Variant2: genIcon('br-variant-v2', <VariantSvg2 />),
  AudienceV2: genIcon('br-audience-v2', <AudienceSvgV2 />),
  CalendarV2: genIcon('br-calendar-v2', <CalendarSvgV2 />),
  ReadOnlyV2: genIcon('br-read-only-v2', <ReadOnlySvgV2 />),
  DownCarrotV2: genIcon('br-down-carrot-v2', <DownCarrotSvgV2 />),
  InactiveCheckboxV1: genIcon('br-down-carrot-v2', <InactiveCheckboxSvgV1 />),
  SuccessCheckV2: genIcon('br-success-check-v2', <SuccessCheckSvgV2 />),
  CrossCheckV2: genIcon('br-cross-check-v2', <CrossCheckSvgV2 />),
  GreaterThanLight: genIcon('br-greater-than-light', <GreaterThanLight />),
  GreaterThanThick: genIcon('br-greater-than-thick', <GreaterThanThick />),
  ImageSearch: genIcon('br-image-search', <ImageSearch />),

  // png based icons
  SortInactive: genIcon('br-sort-inactive', <img src={SortInactivePng} alt="" />),
  SortAsc: genIcon('br-sort-asc', <img src={SortAscPng} alt="" />),
  SortDesc: genIcon('br-sort-desc', <img src={SortDescPng} alt="" />),
  InfoOutlined: genIcon('br-info-outlined', <img src={InfoOutlinedPng} alt="" />),
  WarningOutlined: genIcon('br-warning-outlined', <img src={WarningOutlinedPng} alt="" />),
};

// add pre-colored icons
Object.assign(icons, {
  LockColored: (props: BrIconsProps) => icons.Lock({ ...props, colorStyle: ColorStyles.BLACK }),
  UnlockColored: (props: BrIconsProps) => icons.Unlock({ ...props, colorStyle: ColorStyles.BLACK }),
  BoostColored: (props: BrIconsProps) => icons.Boost({ ...props, colorStyle: ColorStyles.GREEN }),
  BuryColored: (props: BrIconsProps) => icons.Bury({ ...props, colorStyle: ColorStyles.ORANGE }),
  BlacklistColored: (props: BrIconsProps) => icons.Blacklist({ ...props, colorStyle: ColorStyles.RED }),
  IncludeColored: (props: BrIconsProps) => icons.Include({ ...props, colorStyle: ColorStyles.GREEN }),
  ExcludeColored: (props: BrIconsProps) => icons.Exclude({ ...props, colorStyle: ColorStyles.RED }),
  DeleteColored: (props: BrIconsProps) => icons.Delete({ ...props, colorStyle: ColorStyles.BLACK }),
});

// TODO: deprecate following after replace all existing use cases;
const iconComponents = {
  Boost: (props: any) => (props.colored ? icons.BoostColored(props) : icons.Boost(props)),
  Bury: (props: any) => (props.colored ? icons.BuryColored(props) : icons.Bury(props)),
  Blacklist: (props: any) => (props.colored ? icons.BlacklistColored(props) : icons.Blacklist(props)),
  Lock: (props: any) => (props.colored ? icons.LockColored(props) : icons.Lock(props)),
  Unlock: (props: any) => (props.colored ? icons.UnlockColored(props) : icons.Unlock(props)),
  Include: (props: any) => (props.colored ? icons.IncludeColored(props) : icons.Include(props)),
  Exclude: (props: any) => (props.colored ? icons.ExcludeColored(props) : icons.Exclude(props)),
  Delete: (props: any) => (props.colored ? icons.DeleteColored(props) : icons.Delete(props)),
};

const BrIcons = { ...icons, ...iconComponents } as Record<keyof typeof icons, any>;
export default BrIcons;
